import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { file_06 } from "@tql/ui-components-react/untitled/icons";
import { memo, useCallback } from "react";

import { InvoiceStatusBadge } from "~/shared/components/trax-grid/invoice-status-badge/invoice-status-badge.tsx";
import type { Invoice } from "~/shared/schemas/invoice-schema";
import {
	formatCurrency,
	formatDateToMMDDYYYY,
} from "~/shared/utils/string-fns.ts";

import styles from "./invoice-card-mobile.module.css";

type InvoiceCardMobileProps = Readonly<{
	invoice: Invoice;
	isSelected: boolean;
	onSelect: (id: string | number) => void;
	onDeselect: (id: string | number) => void;
	onDocumentClick: (invoice: Invoice) => void;
}>;

const NO_DATA = "N/A";

const formatDate = (value: string | null): string =>
	value ? formatDateToMMDDYYYY(value) : NO_DATA;

const formatAge = (age: number | null): string => {
	if (age === null) return NO_DATA;
	return `${age} Day${age === 1 ? "" : "s"}`;
};

const CardField = ({
	label,
	value,
	testId,
}: {
	label: string;
	value: string;
	testId: string;
}) => (
	<div className={styles.invoiceCardField}>
		<span className={styles.fieldLabel}>{label}</span>
		<span className={styles.fieldValue} data-testid={testId}>
			{value}
		</span>
	</div>
);

export const InvoiceCardMobile = memo<InvoiceCardMobileProps>(
	({
		invoice,
		isSelected,
		onSelect,
		onDeselect,
		onDocumentClick,
	}: InvoiceCardMobileProps) => {
		const handleSelectChange = useCallback(
			(event: { value: boolean }) => {
				if (event.value) {
					onSelect(invoice.invoiceID);
				} else {
					onDeselect(invoice.invoiceID);
				}
			},
			[invoice, onSelect, onDeselect],
		);

		const handleDocumentClick = useCallback(() => {
			onDocumentClick(invoice);
		}, [invoice, onDocumentClick]);

		return (
			<div
				className={styles.invoiceCard}
				data-invoice-id={invoice.invoiceID}
				data-testid={"invoice-card"}
			>
				<div className={styles.invoiceCardHeader}>
					<Checkbox
						aria-label={`Select invoice ${invoice.poNumber ?? invoice.invoiceID}`}
						checked={isSelected}
						onChange={handleSelectChange}
					/>
					<div className={styles.invoiceCardHeaderField}>
						<span className={styles.inlineLabel}>TQL PO:</span>
						<span
							className={styles.inlineValue}
							data-testid={"invoice-card-tql-po"}
						>
							{invoice.poNumber}
						</span>
					</div>
					<div className={styles.statusBadge}>
						<InvoiceStatusBadge invoice={invoice} />
					</div>
				</div>

				<div className={styles.invoiceCardBody}>
					<CardField
						label={"Balance"}
						testId={"invoice-card-balance"}
						value={formatCurrency(invoice.balanceAmt)}
					/>
					<CardField
						label={"Invoice Date"}
						testId={"invoice-card-invoice-date"}
						value={formatDate(invoice.docdate)}
					/>
					<CardField
						label={"Due Date"}
						testId={"invoice-card-due-date"}
						value={formatDate(invoice.dueDate)}
					/>
					<CardField
						label={"Rate"}
						testId={"invoice-card-rate"}
						value={formatCurrency(invoice.invoiceAmt)}
					/>
					<CardField
						label={"Paid"}
						testId={"invoice-card-paid"}
						value={formatCurrency(invoice.paidAmt)}
					/>
					<CardField
						label={"Delivery Date"}
						testId={"invoice-card-delivery-date"}
						value={formatDate(invoice.deliveryDate)}
					/>
					<CardField
						label={"Age"}
						testId={"invoice-card-age"}
						value={formatAge(invoice.age)}
					/>
				</div>

				{Boolean(invoice.custPO) && (
					<div className={styles.invoiceCardCustomerPo}>
						<span className={styles.inlineLabel}>Customer PO:</span>
						<span
							className={styles.customerPoValue}
							data-testid={"invoice-card-customer-po"}
						>
							{invoice.custPO}
						</span>
					</div>
				)}

				<div className={styles.invoiceCardFooter}>
					<span className={styles.footerLabel}>Documents</span>
					<button
						aria-label={"Documents"}
						className={styles.iconButton}
						data-testid={"invoice-card-documents-button"}
						onClick={handleDocumentClick}
						title={"Documents"}
						type={"button"}
					>
						<SvgIcon icon={file_06} size={"large"} />
					</button>
				</div>
			</div>
		);
	},
);

InvoiceCardMobile.displayName = "InvoiceCardMobile";
